import React, { useEffect, useMemo, useState } from "react";

/* ShopLinkCatalog — Ritailink-themed (emerald) */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnon = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Public base URL for your bucket (constant per your message)
const PUBLIC_BUCKET_BASE =
  "https://xfqupvomvhhvvknjanwc.supabase.co/storage/v1/object/public/ritailink-users-media/";
const DEFAULT_BUCKET = "ritailink-users-media";

function sbHeaders() {
  return {
    apikey: supabaseAnon,
    Authorization: `Bearer ${supabaseAnon}`,
    "Content-Type": "application/json",
    Prefer: "return=representation",
  };
}

async function sbSelect({ table, select = "*", match = {}, order }) {
  const url = new URL(`${supabaseUrl}/rest/v1/${table}`);
  url.searchParams.set("select", select);
  Object.entries(match).forEach(([k, v]) => url.searchParams.set(k, `eq.${v}`));
  if (order)
    url.searchParams.set(
      "order",
      `${order.column}.${order.ascending ? "asc" : "desc"}`
    );
  const res = await fetch(url.toString(), { headers: sbHeaders() });
  if (!res.ok) throw new Error(`Select failed: ${res.status}`);
  return res.json();
}

async function sbInsert({ table, values }) {
  const url = `${supabaseUrl}/rest/v1/${table}`;
  const res = await fetch(url, {
    method: "POST",
    headers: sbHeaders(),
    body: JSON.stringify(values),
  });
  if (!res.ok) throw new Error(`Insert failed: ${res.status}`);
  return res.json();
}

async function sbRpc(fn, args) {
  const url = `${supabaseUrl}/rest/v1/rpc/${fn}`;
  const res = await fetch(url, {
    method: "POST",
    headers: sbHeaders(),
    body: JSON.stringify(args || {}),
  });
  if (!res.ok) throw new Error(`RPC ${fn} failed: ${res.status}`);
  return res.json();
}

function buildPublicUrl(filePath) {
  if (!filePath) return "";
  // Guaranteed public URL: base + relative file_path
  return `${PUBLIC_BUCKET_BASE}${filePath}`;
}

// UI utils
function formatPHP(amount) {
  if (amount == null || isNaN(amount)) return "₱0.00";
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
  }).format(Number(amount));
}

const Badge = ({ children }) => (
  <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-emerald-700 border-emerald-200 bg-emerald-50">
    {children}
  </span>
);

const CardSkeleton = () => (
  <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5 p-3 animate-pulse">
    <div className="aspect-square w-full rounded-xl bg-slate-200" />
    <div className="mt-3 h-4 w-3/4 rounded bg-slate-200" />
    <div className="mt-2 h-4 w-1/2 rounded bg-slate-200" />
    <div className="mt-3 h-8 w-full rounded-lg bg-slate-200" />
  </div>
);

function Modal({ open, onClose, children, title }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
          <button
            className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ProductCard({ product, imageUrl, onDetails, onAdd }) {
  const lowStock = product.qty <= 5;
  return (
    <div className="group rounded-2xl bg-white shadow-sm ring-1 ring-black/5 p-3 transition hover:shadow-lg">
      <div className="relative overflow-hidden rounded-xl">
        {imageUrl ? (
          <img
            loading="lazy"
            src={imageUrl}
            alt={product.name}
            className="aspect-square w-full object-cover transition duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="aspect-square w-full bg-slate-100 grid place-items-center text-slate-400">
            No Image
          </div>
        )}
        {product.category && (
          <div className="absolute left-2 top-2">
            <Badge>{product.category}</Badge>
          </div>
        )}
      </div>
      <div className="mt-3 space-y-1">
        <h4 className="line-clamp-2 text-sm font-semibold text-slate-800">
          {product.name}
        </h4>
        <div className="text-sm text-slate-500">{formatPHP(product.price)}</div>
        <div className="flex items-center gap-2 text-xs">
          <span
            className={`font-medium ${
              lowStock ? "text-rose-600" : "text-emerald-700"
            }`}
          >
            {product.qty} in stock
          </span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          onClick={() => onDetails(product)}
          className="rounded-xl border border-emerald-200 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-50"
        >
          Details
        </button>
        <button
          onClick={() => onAdd(product)}
          className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
        >
          Add to Cart
        </button>
      </div>
    </div>
  );
}

// Main component
export default function ShopLinkCatalog({
  clientKey,
  bucket = DEFAULT_BUCKET,
  demoItems = [],
  profileOverride = null,
}) {
  const [store, setStore] = useState({
    business_name: "Shop",
    logo_path: null,
  });
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [detail, setDetail] = useState(null);
  const [imgMap, setImgMap] = useState({});

  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 18;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);

        // Header store info
        if (profileOverride) {
          setStore({
            business_name:
              profileOverride.store_name ||
              profileOverride.business_name ||
              "Shop",
            logo_path: profileOverride.logo_path || null,
          });
        } else {
          const acc = await sbSelect({
            table: "accounts",
            select: "business_name, logo_path, client_key",
            match: { client_key: clientKey },
          });
          if (mounted && acc?.[0]) setStore(acc[0]);
        }

        // Products
        const rows = await sbSelect({
          table: "products",
          select:
            "id,name,description,unit_price,stock_qty,category,file_path,updated_at,tenant_id,is_published",
          match: { tenant_id: clientKey, is_published: true },
          order: { column: "updated_at", ascending: false },
        });
        if (!mounted) return;

        let normalized = (rows || []).map((r) => {
          const firstPath = Array.isArray(r.file_path)
            ? r.file_path[0]
            : r.file_path || "";
          return {
            id: r.id,
            name: r.name,
            description: r.description,
            price: Number(r.unit_price ?? 0),
            qty: Number(r.stock_qty ?? 0),
            category: r.category,
            image_path: firstPath, // relative path
          };
        });

        // Fallback demo items
        if (!normalized.length && demoItems.length) {
          normalized = demoItems.map((d) => ({
            id: d.id,
            name: d.name,
            description: d.description,
            price: Number(d.price ?? 0),
            qty: Number(d.qty ?? 0),
            category: d.category || "Demo",
            image_path: null,
            _demo_image: d.demo_image || "",
          }));
        }

        setProducts(normalized);

        // Categories
        const cats = Array.from(
          new Set(normalized.map((r) => r.category).filter(Boolean))
        );
        setCategories(["All", ...cats]);

        // Build image URLs (public base + relative file_path)
        if (normalized.length) {
          const entries = await Promise.all(
            normalized.map(async (r) => {
              if (r._demo_image) return [r.id, r._demo_image];
              if (!r.image_path) return [r.id, ""];
              const url = buildPublicUrl(r.image_path);
              return [r.id, url];
            })
          );
          if (mounted) setImgMap(Object.fromEntries(entries));
        } else {
          setImgMap({});
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [clientKey, bucket, demoItems, profileOverride]);

  // Filters & pagination
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const catOk = category === "All" || p.category === category;
      const qOk =
        !q ||
        [p.name, p.description, p.category]
          .filter(Boolean)
          .some((s) => s.toLowerCase().includes(q));
      return catOk && qOk;
    });
  }, [products, category, query]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [query, category]);

  function openDetails(p) {
    setDetail(p);
  }

  // Cart helpers
  function addToCart(p) {
    if (!p || !Number.isFinite(Number(p.price)))
      return alert("Invalid product.");
    if (p.qty <= 0) return alert("Sorry, this item is out of stock.");
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.product_id === p.id);
      if (idx >= 0) {
        const copy = [...prev];
        const newQty = Math.min(copy[idx].quantity + 1, p.qty);
        copy[idx] = { ...copy[idx], quantity: newQty };
        return copy;
      }
      const image = imgMap[p.id] || "";
      return [
        ...prev,
        {
          product_id: p.id,
          name: p.name,
          price: Number(p.price || 0),
          quantity: 1,
          image,
          maxQty: p.qty,
        },
      ];
    });
  }
  function changeQty(product_id, qty) {
    setCart((prev) =>
      prev.map((i) =>
        i.product_id === product_id
          ? { ...i, quantity: Math.max(1, Math.min(qty, i.maxQty)) }
          : i
      )
    );
  }
  function removeFromCart(product_id) {
    setCart((prev) => prev.filter((i) => i.product_id !== product_id));
  }

  const cartSubtotal = useMemo(
    () => cart.reduce((s, i) => s + i.price * i.quantity, 0),
    [cart]
  );

  async function checkout(e) {
    e.preventDefault();
    if (cart.length === 0) return alert("Your cart is empty.");
    for (const i of cart) {
      if (i.quantity < 1) return alert("Quantity must be at least 1.");
      if (i.quantity > i.maxQty)
        return alert(`Insufficient stock for ${i.name}.`);
    }
    const form = new FormData(e.currentTarget);
    const buyer_name = String(form.get("buyer_name") || "").trim();
    const buyer_contact = String(form.get("buyer_contact") || "").trim();
    const note = String(form.get("note") || "");
    if (!buyer_name || !buyer_contact)
      return alert("Please enter your name and contact.");

    try {
      const payload = {
        p_client_key: clientKey,
        p_buyer_name: buyer_name,
        p_buyer_contact: buyer_contact,
        p_note: note,
        p_items: cart.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          price: i.price,
        })),
      };
      const res = await sbRpc("place_shop_order", payload);

      await sbInsert({
        table: "shop_orders",
        values: {
          client_key: clientKey,
          product_id: cart[0].product_id,
          product_name: cart.map((i) => i.name).join(", "),
          type: "order",
          quantity: cart.reduce((n, i) => n + i.quantity, 0),
          buyer_name,
          buyer_contact,
          note,
          created_at: new Date().toISOString(),
        },
      });

      alert(
        `Order ${res.order_code || res.id} sent! The store owner will be notified in the Ritailink app.`
      );
      setCart([]);
      setCartOpen(false);
    } catch (err) {
      console.error(err);
      alert(
        err?.message || "Sorry, failed to submit order. Please try again."
      );
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-teal-50">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur supports-[backdrop-filter]:bg-white/60 bg-white/80 border-b border-emerald-100/70">
        <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="h-9 w-9 overflow-hidden rounded-xl ring-1 ring-emerald-100 bg-emerald-50 grid place-items-center text-slate-500">
              {store?.logo_path ? (
                <img
                  src={buildPublicUrl(store.logo_path)}
                  alt={store.business_name || "Store"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-sm">🛍️</span>
              )}
            </div>
            <h1 className="truncate text-lg font-extrabold tracking-tight text-slate-900">
              {store?.business_name || "My Store"}
            </h1>
          </div>

          {/* Search */}
          <div className="hidden flex-1 items-center md:flex">
            <div className="relative w-full max-w-xl">
              <input
                className="w-full rounded-xl border border-emerald-200 bg-white px-4 py-2.5 text-sm text-slate-800 shadow-sm outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-emerald-700/20"
                placeholder="Search products, e.g. 'engine oil', 'helmet'"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                ⌕
              </div>
            </div>
          </div>

          {/* Category + Cart */}
          <div className="ml-auto flex items-center gap-2">
            <select
              className="rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:ring-2 focus:ring-emerald-700/20"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map((c) => (
                <option key={`cat-${c}`} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <button
              onClick={() => setCartOpen(true)}
              className="relative rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-emerald-50"
            >
              🛒 Cart
              {cart.length > 0 && (
                <span className="absolute -right-2 -top-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1 text-xs font-semibold text-white">
                  {cart.length}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Banner */}
      <section className="mx-auto max-w-7xl px-4 pt-6">
        <div className="rounded-3xl bg-gradient-to-r from-emerald-500 to-emerald-500 p-6 sm:p-8 text-white shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight">
                Welcome to {store?.business_name || "our store"}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-emerald-100/90">
                Browse products, check availability, and send an order with
                multiple items. Fast and simple.
              </p>
            </div>
            <div className="flex gap-2">
              <a
                href="#catalog"
                className="rounded-xl bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur hover:bg-white/20"
              >
                Shop now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog */}
      <main id="catalog" className="mx-auto max-w-7xl px-4 py-6">
        {loading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {filtered.length === 0 ? (
              <div className="grid place-items-center rounded-2xl border border-dashed border-emerald-200 bg-white p-12 text-center text-emerald-700">
                No products found.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                {paged.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    imageUrl={imgMap[p.id]}
                    onDetails={openDetails}
                    onAdd={addToCart}
                  />
                ))}
              </div>
            )}

            {filtered.length > pageSize && (
              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-sm text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                <span className="text-sm text-slate-600">
                  Page {page} / {Math.ceil(filtered.length / pageSize)}
                </span>
                <button
                  className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-sm text-emerald-800 hover:bg-emerald-50 disabled:opacity-50"
                  onClick={() =>
                    setPage((p) =>
                      Math.min(Math.ceil(filtered.length / pageSize), p + 1)
                    )
                  }
                  disabled={page >= Math.ceil(filtered.length / pageSize)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Product Details */}
      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.name || "Product Details"}
      >
        {detail && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <img
                  src={imgMap[detail.id] || ""}
                  alt={detail.name}
                  className="w-full rounded-xl object-cover"
                />
              </div>
              <div className="space-y-2">
                <div className="text-2xl font-extrabold text-slate-900">
                  {formatPHP(detail.price)}
                </div>
                <div className="text-sm text-slate-500">
                  <strong>Category:</strong> {detail.category || "—"}
                </div>
                <div className="text-sm text-slate-500">
                  <strong>Available:</strong> {detail.qty}
                </div>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-700">
                  {detail.description || "No description."}
                </p>
                <div className="pt-2 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      addToCart(detail);
                    }}
                    className="rounded-xl border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
                  >
                    Add to Cart
                  </button>
                  <button
                    onClick={() => setCartOpen(true)}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                  >
                    View Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Cart */}
      <Modal
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        title={`Your Cart (${cart.length} item${
          cart.length !== 1 ? "s" : ""
        })`}
      >
        {cart.length === 0 ? (
          <div className="text-slate-600">Your cart is empty.</div>
        ) : (
          <form onSubmit={checkout} className="space-y-4">
            <div className="max-h-72 overflow-auto rounded-xl border border-emerald-200">
              <table className="w-full text-sm">
                <thead className="bg-emerald-50 text-slate-600">
                  <tr>
                    <th className="p-2 text-left">Item</th>
                    <th className="p-2 text-right">Price</th>
                    <th className="p-2 text-center">Qty</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {cart.map((i) => (
                    <tr key={i.product_id} className="border-t">
                      <td className="p-2">
                        <div className="flex items-center gap-3">
                          {i.image ? (
                            <img
                              src={i.image}
                              alt=""
                              className="h-10 w-10 rounded object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded bg-slate-100" />
                          )}
                          <div>
                            <div className="font-medium text-slate-800">
                              {i.name}
                            </div>
                            <div className="text-xs text-slate-500">
                              Max {i.maxQty}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-2 text-right">
                        {formatPHP(i.price)}
                      </td>
                      <td className="p-2 text-center">
                        <input
                          type="number"
                          min={1}
                          max={i.maxQty}
                          value={i.quantity}
                          onChange={(e) =>
                            changeQty(i.product_id, Number(e.target.value))
                          }
                          className="w-16 rounded border border-emerald-200 px-2 py-1 text-center"
                        />
                      </td>
                      <td className="p-2 text-right font-medium">
                        {formatPHP(i.price * i.quantity)}
                      </td>
                      <td className="p-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeFromCart(i.product_id)}
                          className="text-rose-600 hover:underline"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="text-slate-600">Subtotal</div>
              <div className="text-lg font-extrabold text-slate-900">
                {formatPHP(cartSubtotal)}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">
                  Full Name
                </label>
                <input
                  name="buyer_name"
                  required
                  className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-700/20"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">
                  Mobile / Email
                </label>
                <input
                  name="buyer_contact"
                  required
                  className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-700/20"
                />
              </div>
              <div className="sm:col-span-2 space-y-1">
                <label className="text-xs font-medium text-slate-600">
                  Message / Notes
                </label>
                <textarea
                  name="note"
                  rows={3}
                  className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm"
                  placeholder="Optional message to seller..."
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setCartOpen(false)}
                className="rounded-xl border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
              >
                Continue Shopping
              </button>
              <button
                type="submit"
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >
                Submit Order
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* Footer */}
      <footer className="mt-10 border-t border-emerald-100 bg-white/70">
        <div className="mx-auto max-w-7xl px-4 py-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {store?.business_name || "Shop"} •
          Powered by Ritailink
        </div>
      </footer>
    </div>
  );
}
